<template>
  <div
    :class="[
      {
        'p-absolute': !relative,
        'p-relative': relative
      },
      'video-embed w-100 h-100 d-flex align-items-center justify-content-center'
    ]"
    ref="video-embed"
  >
    <transition name="fade">
      <div
        v-if="!isPlaying"
        class="video-embed__thumbnail p-overlay z-3"
        :style="{
          backgroundImage: `url(${thumbnailUrl})`,
          opacity: thumbnailOpacity
        }"
      ></div>
    </transition>

    <transition name="fade">
      <div
        v-if="
          isBuilderContext() ||
            !this.isInitialized ||
            ((this.isYoutube || this.isMux) && this.autoplay && !this.isPlaying)
        "
        @click="toggle"
        class="p-overlay video-embed__controls-overlay z-3"
      >
        <play-button
          :is-loading="isLoading"
          data-cy="play"
          plyr="play"
          class="video-embed__play"
        />
      </div>
    </transition>

    <div
      @click="preventNavigation"
      data-cy="video-embed"
      :class="[
        {
          '--playing': isPlaying,
          '--loading': isLoading,
          '--muted': isMuted,
          '--has-interacted': hasUserInteracted,
          '--vimeo': isVimeo,
          '--youtube': isYoutube,
          '--mux': isMux
        },
        'video-embed__video w-100 h-100'
      ]"
    >
      <template v-if="isFacebook">
        <div
          class="facebook-video fb-video"
          data-role="facebook-video"
          :data-href="video.link"
          data-width="auto"
          data-allowfullscreen="false"
          data-autoplay="true"
          data-controls="false"
        ></div>
      </template>
      <template v-else-if="isMux">
        <video
          v-if="!isBuilderContext()"
          controls
          crossorigin
          playsinline
          :id="playerId"
        ></video>
      </template>
      <div v-else-if="isMounted" class="plyr__video-embed" :id="playerId">
        <iframe
          style="border: none;"
          :src="embedLink"
          allowfullscreen
          allowtransparency
          allow="autoplay; fullscreen;"
        ></iframe>
      </div>

      <div
        v-if="!hideControls && isPlaying && isMuted"
        class="video-embed__tooltip --muted"
        @click="unmute"
      >
        Tap to unmute
      </div>
    </div>
  </div>
</template>

<script>
import Plyr from "plyr"
import Hls from "hls.js"

import { mapState, mapMutations, mapActions } from "vuex"

import { isPresent, isBuilderContext } from "@/lib/utils"
import {
  appendEmbedParams,
  generateMuxThumbnailUrl,
  isYoutube,
  isFacebook,
  isVimeo,
  isMux,
  initializeFacebook
} from "@/lib/video-utils"
import { isIos } from "@/lib/is-platform"
import { triggerBlockAction } from "@/lib/stream-utils"

const ERRORS = {
  NOT_ALLOWED_ERROR: "NotAllowedError"
}

// https://developers.google.com/youtube/iframe_api_reference#onStateChange
const YT_STATES = {
  UNSTARTED: -1,
  ENDED: 1,
  PLAYING: 2,
  PAUSED: 2,
  BUFFERING: 3,
  VIDEO_CUED: 5
}

const DEFAULT_ASPECT_RATIO = "16:9"

const wrapPlayer = (videoPlayer, vm) => ({
  play() {
    if (videoPlayer && videoPlayer.play) {
      videoPlayer.play()?.catch(e => {
        if (e.name === ERRORS.NOT_ALLOWED_ERROR) {
          vm.handleNotAllowedError()
        }
      })
    }
    vm.$emit("play")
    vm.isLoading = true
    vm.isPaused = false
  },

  stop() {
    videoPlayer.stop()
    vm.$emit("stop")
    vm.isLoading = false
    vm.isPlaying = false
    vm.isPaused = false
  },

  pause() {
    videoPlayer.pause()
    vm.$emit("pause")
    vm.isPaused = true
  },

  isMuted() {
    vm.isFacebook ? videoPlayer.isMuted() : videoPlayer.muted
  },

  unmute() {
    vm.isFacebook ? videoPlayer.unmute() : (videoPlayer.muted = false)
  }
})

export default {
  components: {
    PlayButton: () => import("@/components/PlayButton")
  },

  props: {
    video: { type: Object, required: true },
    thumbnail: { type: String, default: null },
    thumbnailOpacity: { type: [String, Number], default: 1 },
    autoplay: { type: Boolean, default: false },
    hideControls: { type: Boolean, default: false },
    hidePlayButton: { type: Boolean, default: false },
    disableFullscreen: { type: Boolean, default: false },
    autoInitialize: { type: Boolean, default: false },
    playsInline: { type: Boolean, default: true },
    portrait: { type: Boolean, default: false },
    muted: { type: Boolean, default: false },
    loop: { type: Boolean, default: false },
    cover: { type: Boolean, default: false },
    aspectRatio: { type: String, default: null },
    finishedAction: { type: Object, default: null },
    relative: { type: Boolean, default: false }
  },

  data() {
    return {
      isLoading: false,
      isMounted: false,
      isInitialized: false,
      isInitializing: false,
      isSeeking: false,
      isPlaying: false,
      isPaused: false,
      isMuted: false,
      isFullscreenNative: false,
      // Plyr emits fullscreen events globally, so we need to track if
      // this particular video instance was fullscreened as we cannot rely
      // on the Plyr events
      //
      // https://github.com/sampotts/plyr/issues/2424
      isFullscreenedVideo: false,
      isMountingFacebookPlayer: false,
      hasUserInteracted: false,
      hasTrackedInitialPlay: false,
      seekDebounce: null,
      plyr: null,
      player: null
    }
  },

  computed: {
    ...mapState("videos", ["isFullscreen"]),

    embedLink() {
      return appendEmbedParams(this.video.provider, this.video.embed_link)
    },

    playerId() {
      return `video-embed-${this._uid}`
    },

    isFacebook() {
      return isFacebook(this.video.provider)
    },

    isMux() {
      return isMux(this.video.provider)
    },

    isVimeo() {
      return isVimeo(this.video.provider)
    },

    isYoutube() {
      return isYoutube(this.video.provider)
    },

    shouldTrackAnalytics() {
      return !this.autoplay
    },

    thumbnailUrl() {
      return isPresent(this.thumbnail)
        ? this.thumbnail
        : this.video.thumbnail_url
        ? this.video.thumbnail_url
        : this.isMux
        ? generateMuxThumbnailUrl(this.video.embed_link)
        : null
    },

    ratio() {
      const aspectRatio =
        this.aspectRatio || this.video.aspect_ratio || DEFAULT_ASPECT_RATIO

      // Assume all YT videos are 16 / 9
      return !isYoutube(this.video.provider) &&
        this.portrait &&
        aspectRatio === DEFAULT_ASPECT_RATIO
        ? 9 / 16
        : aspectRatio
            .split(":")
            .map(v => parseFloat(v))
            .reduce((acc, val) => acc / val)
    }
  },

  methods: {
    isBuilderContext,

    ...mapActions("analytics", ["trackVideoStarted", "trackVideoWatched"]),
    ...mapMutations("videos", ["setFullscreen"]),

    initalizePlayer() {
      return new Promise(resolve => {
        if (this.isInitializing) return resolve()
        if (this.isInitialized) return resolve()

        this.isMounted = true
        this.isLoading = this.autoplay

        this.$nextTick(() => {
          // Recheck after tick
          if (this.isInitializing) return resolve()
          if (this.isInitialized) return resolve()

          this.isInitializing = true

          const fullscreen = {
            enabled: !this.disableFullscreen,
            ...(!this.isMux && this.playsInline ? {} : { iosNative: true })
          }

          const video = document.querySelector(`#${this.playerId}`)

          // Ensure player is muted if not already initialized,
          // so video plays on iOS
          this.isMuted = this.muted || (isIos() && !this.autoInitialize)

          this.plyr = new Plyr(video, {
            controls: this.hideControls
              ? ["play-large"]
              : ["play-large", "progress", "fullscreen", "airplay", "mute"],
            hideControls: this.hideControls,
            autoplay: this.autoplay,
            muted: this.isMuted,
            playsinline: this.playsInline,
            storage: { enabled: false },
            loop: { active: this.loop },
            vimeo: { background: 1 },
            fullscreen
          })

          if (this.isMux) {
            if (video.canPlayType("application/vnd.apple.mpegurl")) {
              // Some browsers, like iOS Safari, support HLS Natively
              // https://docs.mux.com/guides/video/play-your-videos#3-use-the-hls-url-in-a-player
              video.src = this.embedLink
            } else if (Hls.isSupported()) {
              const hls = new Hls()
              hls.loadSource(this.embedLink)
              hls.attachMedia(video)
            } else {
              // TODO: Figure out fallback here?
              // Not sure when this would ever happen
            }
          }

          // Component can be destroyed when navigating away
          if (this.plyr) {
            this.plyr.muted = this.isMuted

            this.plyr.on("error", e => {
              if (e.detail.name === ERRORS.NOT_ALLOWED_ERROR) {
                this.handleNotAllowedError()
              }
            })

            this.plyr.on("ready", () => {
              this.isLoading = false
              this.isInitialized = true
              this.isInitializing = false

              this.setupFullscreenWatcher()
              this.ensureVideoHeight()

              this.$nextTick(() => {
                this.$refs["video-embed"]
                  .querySelector(".plyr > [data-plyr=play]")
                  .addEventListener("click", () => {
                    if (!this.isInitialized) return

                    this.isLoading = !this.isPaused
                    this.hasUserInteracted = true
                  })
              })

              this.$emit("ready", this.player)
              resolve()
            })

            this.plyr.on("ended", () => {
              this.$emit("ended")
              this.isPlaying = false
              this.isPaused = false

              this.handleVideoEnd()
            })

            this.plyr.on("play", () => {
              this.isLoading = true
              this.isPlaying = false
            })

            this.plyr.on("playing", () => {
              if (this.plyr.muted) {
                this.isMuted = true
              }

              if (this.shouldTrackAnalytics && !this.hasTrackedInitialPlay) {
                this.trackVideoStarted({ video: this.video })
              }

              this.isPlaying = true
              this.isPaused = false
              this.isLoading = false
              this.hasTrackedInitialPlay = true

              this.$emit("play")

              this.ensureVideoHeight()
            })

            this.plyr.on("pause", () => {
              this.isPaused = true
              this.isLoading = false
              this.$emit("pause")
            })

            this.plyr.on("seeking", () => {
              this.isSeeking = true

              clearTimeout(this.seekDebounce)
              this.seekDebounce = setTimeout(() => {
                this.isSeeking = false
              }, 400)
            })

            this.plyr.on("enterfullscreen", () => {
              // See comment on isFullscreenedVideo data attribute
              // https://github.com/sampotts/plyr/issues/2424
              if (!this.isFullscreenedVideo) return

              this.setFullscreen(true)
              this.isFullscreenNative =
                isIos() &&
                !isYoutube(this.video.provider) &&
                !!this.plyr.embed?.requestFullscreen
              this.resizeVideoPlayer()
            })

            this.plyr.on("exitfullscreen", () => {
              // See comment on isFullscreenedVideo data attribute
              // https://github.com/sampotts/plyr/issues/2424
              if (!this.isFullscreenedVideo) return

              this.isFullscreenedVideo = false
              this.setFullscreen(false)
              this.resizeVideoPlayer()
            })

            this.plyr.on("statechange", e => {
              if (this.autoplay && e.detail.code === YT_STATES.UNSTARTED) {
                this.isPlaying = false
                this.isPaused = false
                this.isLoading = false
              }

              if (e.detail.code === YT_STATES.BUFFERING) {
                this.isLoading = !this.isPaused
              }
            })

            this.player = wrapPlayer(this.plyr, this)
          }
        })
      })
    },

    unmute() {
      if (this.plyr) this.plyr.muted = false
      this.isMuted = false
    },

    ensureVideoHeight() {
      this.resizeVideoPlayer()

      this.$nextTick(() => {
        setTimeout(this.resizeVideoPlayer, 1000)
        setTimeout(this.resizeVideoPlayer, 2000)
      })
    },

    handleNotAllowedError() {
      this.isLoading = false
      this.isPaused = true
    },

    setupFacebookPlayer() {
      this.isLoading = true

      window.FB.Event.subscribe("xfbml.ready", msg => {
        this.isLoading = false
        this.isInitialized = true

        this.isMountingFacebookPlayer = true

        if (msg.type === "video") {
          const fbPlayer = msg.instance
          this.player = wrapPlayer(fbPlayer, this)

          fbPlayer.subscribe("startedPlaying", () => {
            this.player.unmute()

            if (this.isMountingFacebookPlayer) {
              this.player.pause()
              return
            }

            if (this.player.isMuted()) {
              this.player.pause()
            } else {
              this.isPlaying = true
              this.isPaused = false
            }

            if (!this.isPaused && !this.player.isMuted()) {
              this.hasUserInteracted = true
            }

            this.$emit("play")
          })

          fbPlayer.subscribe("paused", () => {
            if (this.isMountingFacebookPlayer) this.hasUserInteracted = false
            this.isPaused = true
            this.$emit("pause")
          })

          fbPlayer.subscribe("finishedPlaying", () => {
            this.$emit("ended")
            this.handleVideoEnd()
          })

          // Used to detect if can play video with sound
          setTimeout(() => {
            this.isMountingFacebookPlayer = false
            this.$emit("ready", this.player)
          }, 500)

          this.$nextTick(() => {
            setTimeout(this.resizeFacebookVideo, 1000)
            setTimeout(this.resizeFacebookVideo, 2000)
          })
        }
      })
    },

    resizeFacebookVideo() {
      const fbVideoFrame = document.querySelector(
        "[data-role=facebook-video] iframe"
      )

      const wrapperHeight = this.$refs["video-embed"].getBoundingClientRect()
        .height
      const videoHeight = fbVideoFrame.getBoundingClientRect().height

      if (!this.originalVideoHeight || videoHeight > this.originalVideoHeight) {
        this.originalVideoHeight = videoHeight
      }

      if (Math.abs(wrapperHeight - this.originalVideoHeight) < 10) {
        return
      }

      const maxWidthPercent =
        Math.min(1, wrapperHeight / this.originalVideoHeight) * 100

      fbVideoFrame.style.maxWidth = `${maxWidthPercent}%`
      fbVideoFrame.style.left = `${(100 - maxWidthPercent) / 2}%`
    },

    preventNavigation(e) {
      e.stopPropagation()
      e.preventDefault()
    },

    toggle(e) {
      this.preventNavigation(e)

      if (this.isInitializing) return
      if (this.autoInitialize && !this.isInitialized) return
      if (this.isLoading) return

      this.hasUserInteracted = true

      if (!isBuilderContext()) {
        if (this.isPaused || !this.isPlaying) {
          this.isLoading = true

          if (!this.isInitialized) {
            this.initalizePlayer().then(() => {
              this.player.play()
            })
          } else {
            this.player.play()
          }
        } else {
          this.player.pause()
        }
      }
    },

    teardown() {
      if (this.shouldTrackAnalytics && this.hasTrackedInitialPlay) {
        this.trackVideoWatched({
          video: this.video,
          watchedSeconds: this.plyr.currentTime,
          watchedPercentage: (this.plyr.currentTime / this.plyr.duration) * 100
        })
      }

      this.isLoading = false
      this.isPaused = false
      this.isPlaying = false
      this.isSeeking = false
      this.isMounted = false
      this.isInitialized = false
      this.hasTrackedInitialPlay = false

      if (this.plyr) this.plyr.destroy()
    },

    setupFullscreenWatcher() {
      const element = this.$refs["video-embed"]?.querySelector(
        "[data-plyr=fullscreen]"
      )

      if (element) {
        element.addEventListener("click", async e => {
          this.isFullscreenedVideo = true

          if (isIos()) {
            e.preventDefault()
            e.stopPropagation()

            if (this.plyr.embed.requestFullscreen) {
              try {
                await this.plyr.embed.requestFullscreen()
                this.plyr.fullscreen.exit()
              } catch (e) {
                // Swallow errors
              }
            }
          }
        })
      }
    },

    handleSetup() {
      if (this.isFacebook) {
        initializeFacebook(() => {
          this.setupFacebookPlayer()
        })
      } else if (this.autoInitialize) {
        this.initalizePlayer()
      } else if (this.autoplay) {
        this.isLoading = true
        this.initalizePlayer().then(() => {
          this.player.play()
        })
      }
    },

    resizeVideoPlayer() {
      const videoEmbed = this.$refs["video-embed"]

      if (!videoEmbed) return

      const { width, height } = this.isFullscreen
        ? { width: window.innerWidth, height: window.innerHeight }
        : videoEmbed.getBoundingClientRect()

      if (!this.isMux && this.plyr.elements) {
        this.plyr.elements.wrapper.style.paddingBottom = height + "px"
      }

      // TODO: Take into account height for cover
      // Currently only width is taken into account for determining width / height
      // for a video set to "cover"
      //
      // On a mobile phone this is enough, but on desktop it can result in some black
      // bars on the sides of the video

      if (
        (this.isMux || isYoutube(this.video.provider)) &&
        (this.portrait || this.cover)
      ) {
        const scale = Math.max(height / (width / this.ratio), 1)

        if (this.plyr && this.plyr.elements) {
          this.plyr.elements.wrapper.style.transform = `scale(${scale})`
        }
      }

      if (isVimeo(this.video.provider)) {
        // Vimeo was not being centered vertically and Plyr uses a hack
        // to push the video controls off the screen.
        // This approach was using a % based system and was resulting in
        // our videos not being positioned properly. Can read more here:
        // https://github.com/sampotts/plyr/issues/697
        //
        // The following approach uses the height of the container and a
        // fixed value of 200px to push the controls off the screen by 100px
        //
        // If its a portrait video it also contrains the width to ensure the
        // video stays within the container
        const embedWrapper = this.plyr.elements?.wrapper.querySelector(
          ".plyr__video-embed__container"
        )

        const nativeControlsPadding = this.isFullscreen ? 0 : 200

        embedWrapper.style.transform = `translateY(-${nativeControlsPadding /
          2}px)`
        embedWrapper.style.paddingBottom = `${height + nativeControlsPadding}px`

        const embedWidth =
          this.cover || this.portrait
            ? Math.max(height * this.ratio, width) // Ensures 100% height
            : Math.min(height * this.ratio, width) // Ensures width does not exceed container

        embedWrapper.style.width = `${embedWidth}px`
        embedWrapper.style.margin = "0 auto"

        if ((this.cover || this.portrait) && embedWidth > width) {
          // Re-center video if it exceeds container
          embedWrapper.style.left = `-${(embedWidth - width) / 2}px`
        } else {
          embedWrapper.style.left = "0px"
        }
      } else if (
        isPresent(this.plyr.elements) &&
        isPresent(this.plyr.elements.wrapper)
      ) {
        if (this.isFullscreen) {
          this.plyr.elements.wrapper.style.paddingBottom = height
        } else {
          this.plyr.elements.wrapper.style.paddingBottom = null
        }
      }
    },

    handleVideoEnd() {
      this.hasTrackedInitialPlay = false

      if (this.finishedAction) {
        triggerBlockAction(this.finishedAction)
      }

      this.trackVideoWatched({
        video: this.video,
        watchedSeconds: this.plyr.duration,
        watchedPercentage: 100
      })
    },

    handleOrientationChange() {
      setTimeout(() => this.resizeVideoPlayer(), 500)
      setTimeout(() => this.resizeVideoPlayer(), 750)
      setTimeout(() => this.resizeVideoPlayer(), 1000)
    }
  },

  mounted() {
    if (isBuilderContext()) return

    this.handleSetup()
    // TODO: this is deprecated, use screen.orientation.onchange instead
    window.addEventListener("orientationchange", this.handleOrientationChange)
  },

  beforeDestroy() {
    if (this.plyr) this.teardown()
    window.removeEventListener(
      "orientationchange",
      this.handleOrientationChange
    )
  },

  deactivated() {
    this.teardown()
  },

  activated() {
    this.handleSetup()
  }
}
</script>

<style lang="scss">
@use 'sass:math';

@import "~plyr/dist/plyr.css";
@import "@/assets/styles/variables";

$plyr-controls--height: 44px;

.video-embed__thumbnail {
  background-size: cover;
  background-position: 50% 50%;
  pointer-events: none;
}

.video-embed__video {
  position: absolute;
  top: 0;

  &:hover {
    cursor: pointer;
  }

  .plyr__controls {
    opacity: 0;
    z-index: $video--controls-z-index;
  }

  .plyr--full-ui.plyr--video .plyr__control--overlaid {
    color: $color-ui--white;
    background: rgba($color-ui--black, 0.56);
    height: $size--10;
    width: $size--10;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: $video--controls-z-index;

    svg {
      height: 24px;
      width: 24px;
    }
  }

  .plyr__video-wrapper {
    iframe {
      opacity: 0.01;
      background: $color-ui--black;
    }
  }

  &.--loading {
    .plyr--full-ui.plyr--video:not(.plyr--paused) .plyr__control--overlaid {
      svg {
        display: none;
      }

      .plyr__sr-only,
      .plyr__sr-only:after {
        border-radius: 50%;
      }
      .plyr__sr-only {
        // Override Plyr default element styles
        clip: initial;
        border: initial !important;
        overflow: initial;
        padding: initial !important;
        position: initial !important;
        width: 2rem !important;
        height: 2rem !important;

        margin: 0;
        position: relative;
        text-indent: -9999em;
        border-top: 3px solid rgba(255, 255, 255, 0) !important;
        border-right: 3px solid #ffffff !important;
        border-bottom: 3px solid #ffffff !important;
        border-left: 3px solid #ffffff !important;
        transform: translateZ(0);
        animation: spin 1.1s infinite ease;
      }
    }
  }

  &.--has-interacted,
  &.--playing {
    .plyr__controls {
      opacity: 1;
    }
  }

  &.--playing {
    .plyr__video-wrapper {
      iframe {
        opacity: 1;
      }
    }

    &.--muted {
      .plyr__volume {
        position: relative;

        button {
          position: relative;
          z-index: 2;
        }

        &:after {
          content: "";
          opacity: 0;
          pointer-events: none;
          position: absolute;
          z-index: 1;
          height: 32px;
          width: 32px;
          left: 2px;
          top: 2px;
          background: rgba($color-ui--black, 0.24);
          border-radius: 50px;
          animation: pulse 1.4s ease-in-out;
          animation-iteration-count: 2;
        }
      }
    }
  }

  & > .plyr {
    height: 100%;
    z-index: initial;

    &.plyr--fullscreen-fallback {
      z-index: 10000000;
    }
  }

  // TODO: Figure out if this is requried
  .plyr__poster {
    z-index: 1;
  }

  .plyr__video-embed iframe {
    z-index: 2;
  }
}

.video-embed__play {
  position: relative;
  top: math.div($plyr-controls--height, 2);
}

.video-embed__controls-overlay {
  height: calc(100% - #{$plyr-controls--height});
  z-index: $video--controls-z-index;

  &.--click-through {
    pointer-events: none;
  }

  &:hover {
    cursor: pointer;
  }
}

.video-embed__tooltip {
  position: absolute;
  z-index: 100;
  background: $color-ui--black;
  border-radius: $size--2;
  padding: $size--2;
  color: $color-ui--white;
  font-size: 12px;
  opacity: 0;
  border: 2px solid white;
  text-align: center;

  animation: fade-in-and-out 8s ease forwards;
  animation-delay: 1s;

  &.--muted {
    bottom: $plyr-controls--height;
    right: $size--2;

    &:after {
      content: "";
      position: absolute;
      bottom: -12px;
      right: $size--3;
      border: 6px solid #000;
      border-color: white transparent transparent transparent;
    }
  }
}

// https://github.com/sampotts/plyr#customizing-the-css
:root {
  --plyr-color-main: $color-ui--grey-200;
}

.plyr.plyr--video {
  .plyr__controls {
    height: $plyr-controls--height;
    padding: 0 $size--3 $size--2 $size--4;
  }

  .plyr__video-wrapper {
    max-height: 100%;
  }

  // Fixes thumb being off track
  // input[type="range"]::-webkit-slider-thumb {
  //   margin-top: calc(
  //     var(--plyr-range-thumb-height, 13px) / 2 * -1 -
  //       var(--plyr-range-track-height, 5px) / 2 * -1
  //   );
  // }

  .plyr__volume {
    width: initial;
    min-width: initial;
  }

  .plyr__control[data-plyr="fullscreen"] {
    margin-left: $size--4;
  }
}

@media screen and (min-width: $breakpoint--lg) {
  .plyr.plyr--video {
    &:fullscreen,
    &:-webkit-full-screen {
      .plyr__video-embed__container {
        padding-bottom: 100vh !important;
        transform: translateY(0) !important;
      }
    }
  }
}

@keyframes pulse {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }

  10% {
    opacity: 1;
    transform: scale(1);
  }

  70% {
    opacity: 1;
    transform: scale(1.6);
  }

  100% {
    opacity: 0;
    transform: scale(2.2);
  }
}
</style>
